import {createSelector} from 'reselect';

import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {InboxesSearchLocationsEnum} from '../../../models/url/inboxesUrlModel';
import {getPanels} from '../panelsSelectors';
import {InboxesPanelId, PanelTypesEnum} from '../panelStateBase';
import {InboxesPanelState} from './inboxesPanelState';
import {hasSearchInInboxesParameters, InboxesParameters} from './parameters/inboxesParameters';
import {buildParametersPath} from './parameters/inboxesParametersPathHelpers';

/*
 * Panel selectors.
 */

export interface InboxesPanelProps {
  panelId: InboxesPanelId;
}

export type InboxesPanelIdSelector<P> = AppParametricSelector<P, InboxesPanelId>;

function getInboxesPanelId(_state: unknown, props: InboxesPanelProps) {
  return props.panelId;
}

export type InboxesPanelSelector<P> = AppParametricSelector<P, InboxesPanelState>;

export function makeGetInboxesPanel(): InboxesPanelSelector<InboxesPanelProps>;
export function makeGetInboxesPanel<P>(getPanelId: InboxesPanelIdSelector<P>): InboxesPanelSelector<P>;
export function makeGetInboxesPanel(getPanelId: InboxesPanelIdSelector<any> = getInboxesPanelId) {
  const getInboxesPanel = makeGetInboxesPanelOptional(getPanelId);
  return createSelector([getInboxesPanel], (panel) => {
    if (!panel) {
      throw new Error('Inboxes panel not found.');
    }

    return panel;
  });
}

export function makeGetInboxesPanelOptional(getPanelId: InboxesPanelIdSelector<any> = getInboxesPanelId) {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];

    if (!panel || panel.type !== PanelTypesEnum.INBOXES) {
      return null;
    }

    return panel;
  });
}

export type InboxesParametersSelector<P> = AppParametricSelector<P, InboxesParameters>;

export function makeGetInboxesParameters(): InboxesParametersSelector<InboxesPanelProps>;
export function makeGetInboxesParameters<P>(
  getPanelId: InboxesPanelIdSelector<P>,
): InboxesParametersSelector<P>;
export function makeGetInboxesParameters(getPanelId: InboxesPanelIdSelector<any> = getInboxesPanelId) {
  const getInboxesPanel = makeGetInboxesPanel(getPanelId);

  return createSelector(getInboxesPanel, ({parameters}) => parameters);
}

export type InboxesIsEditingWorkspacesSelector<P> = AppParametricSelector<P, boolean>;

export function makeGetInboxesIsEditingWorkspaces(): InboxesIsEditingWorkspacesSelector<InboxesPanelProps>;
export function makeGetInboxesIsEditingWorkspaces<P>(
  getPanelId: InboxesPanelIdSelector<P>,
): InboxesIsEditingWorkspacesSelector<P>;
export function makeGetInboxesIsEditingWorkspaces(
  getPanelId: InboxesPanelIdSelector<any> = getInboxesPanelId,
) {
  const getInboxesPanel = makeGetInboxesPanel(getPanelId);

  return createSelector(getInboxesPanel, ({isEditingWorkspaces}) => isEditingWorkspaces);
}

export function makeGetInboxesPath() {
  const getInboxesParameters = makeGetInboxesParameters();

  return createSelector(getInboxesParameters, (inboxesParameters) => buildParametersPath(inboxesParameters));
}

export function makeGetPinTagBannerErrors(getInboxesPanel = makeGetInboxesPanel()) {
  return createSelector(getInboxesPanel, ({pinTagBannerErrors}) => pinTagBannerErrors);
}

export function makeGetIsSearchQueryBuilderDropdownOpen() {
  return createSelector(
    makeGetInboxesPanel(),
    (inboxesPanel) => inboxesPanel.isSearchQueryBuilderDropdownOpen,
  );
}

export function makeGetIsSearching() {
  const getInboxesParameters = makeGetInboxesParameters();

  return createSelector(getInboxesParameters, hasSearchInInboxesParameters);
}

export function makeGetIsSearchingLocally() {
  const getInboxesParameters = makeGetInboxesParameters();

  return createSelector(
    getInboxesParameters,
    (inboxesParameters) =>
      hasSearchInInboxesParameters(inboxesParameters) &&
      inboxesParameters.search.location === InboxesSearchLocationsEnum.LOCAL,
  );
}
